import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiHeart, FiCoffee, FiAward, FiStar, FiZap } = FiIcons;

const SupportPopup = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('onetime');

  // 禁用背景滾動
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const supportOptions = [
    // 一次性支持
    {
      amount: 5,
      paymentLink: "https://buy.stripe.com/aFa14o6qL2Gx1vh5iq0Fi0I",
      currency: "usd",
      icon: FiCoffee,
      color: "from-blue-500 to-cyan-500",
      titleKey: "supportTier1Title",
      descKey: "supportTier1Desc",
      type: "onetime"
    },
    {
      amount: 8,
      paymentLink: "https://buy.stripe.com/bJe9AUg1lbd35LxcKS0Fi0J",
      currency: "usd",
      icon: FiHeart,
      color: "from-purple-500 to-pink-500",
      titleKey: "supportTier2Title",
      descKey: "supportTier2Desc",
      popular: true,
      type: "onetime"
    },
    {
      amount: 10,
      paymentLink: "https://buy.stripe.com/4gM8wQeXhdlbei3eT00Fi0H",
      currency: "usd",
      icon: FiAward,
      color: "from-yellow-500 to-orange-500",
      titleKey: "supportTier3Title",
      descKey: "supportTier3Desc",
      type: "onetime"
    },
    // 每月訂閱
    {
      amount: 5,
      paymentLink: "https://buy.stripe.com/cNi6oIg1lch7c9V6mu0Fi0N",
      currency: "usd",
      icon: FiCoffee,
      color: "from-green-500 to-emerald-500",
      titleKey: "monthlyTier1Title",
      descKey: "monthlyTier1Desc",
      type: "monthly"
    },
    {
      amount: 8,
      paymentLink: "https://buy.stripe.com/bJe6oIg1lbd32zleT00Fi0O",
      currency: "usd",
      icon: FiZap,
      color: "from-indigo-500 to-purple-500",
      titleKey: "monthlyTier2Title",
      descKey: "monthlyTier2Desc",
      type: "monthly",
      popular: true
    },
    {
      amount: 10,
      paymentLink: "https://buy.stripe.com/8x25kEeXhftj6PB12a0Fi0P",
      currency: "usd",
      icon: FiAward,
      color: "from-orange-500 to-red-500",
      titleKey: "monthlyTier3Title",
      descKey: "monthlyTier3Desc",
      type: "monthly"
    }
  ];

  const handleSupportClick = (paymentLink) => {
    window.open(paymentLink, '_blank', 'noopener,noreferrer');
  };

  const currentOptions = supportOptions.filter(opt => opt.type === activeTab);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* 背景遮罩 - 提高 z-index */}
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* 彈窗容器 - 確保可滾動且內容可見 */}
          <div 
            className="fixed inset-0 z-[9999] overflow-y-auto"
            style={{ 
              paddingTop: 'env(safe-area-inset-top)',
              paddingBottom: 'env(safe-area-inset-bottom)'
            }}
          >
            <div 
              className="min-h-full flex items-center justify-center p-4 py-8"
              onClick={onClose}
            >
              <motion.div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl relative"
                initial={{ scale: 0.9, y: 20, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.9, y: 20, opacity: 0 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* 關閉按鈕 - 固定在右上角 */}
                <motion.button
                  className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                  onClick={onClose}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <SafeIcon icon={FiX} className="w-6 h-6 text-gray-600" />
                </motion.button>

                <div className="p-6 sm:p-8 pt-16 sm:pt-8">
                  {/* 頂部標題區 */}
                  <div className="mb-6">
                    <motion.div
                      className="flex items-center gap-3 mb-3 justify-center sm:justify-start"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <motion.div
                        animate={{
                          scale: [1, 1.1, 1],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          repeatType: "reverse",
                          ease: "easeInOut"
                        }}
                      >
                        <SafeIcon icon={FiHeart} className="w-8 h-8 text-pink-500 flex-shrink-0" />
                      </motion.div>
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-800 text-center sm:text-left">
                        {t('supportPopupTitle')}
                      </h3>
                    </motion.div>
                    
                    <motion.p
                      className="text-sm sm:text-base text-gray-600 leading-relaxed text-center sm:text-left"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 }}
                    >
                      {t('supportPopupDescription')}
                    </motion.p>
                  </div>

                  <motion.p
                    className="text-center text-gray-600 leading-relaxed mb-6 text-sm sm:text-base"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    {t('supportPopupSubtitle')}
                  </motion.p>

                  {/* Tab 切換按鈕 */}
                  <div className="flex gap-2 sm:gap-3 mb-6 sm:mb-8 bg-gray-100 p-1.5 rounded-xl max-w-md mx-auto">
                    <motion.button
                      className={`flex-1 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold text-xs sm:text-base transition-all duration-300 ${
                        activeTab === 'onetime'
                          ? 'bg-white text-purple-600 shadow-md'
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                      onClick={() => setActiveTab('onetime')}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {t('onetimeSupport')}
                    </motion.button>
                    <motion.button
                      className={`flex-1 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold text-xs sm:text-base transition-all duration-300 ${
                        activeTab === 'monthly'
                          ? 'bg-white text-green-600 shadow-md'
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                      onClick={() => setActiveTab('monthly')}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {t('monthlySubscription')}
                    </motion.button>
                  </div>

                  {/* 方案卡片 */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeTab}
                      className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      {currentOptions.map((option, index) => (
                        <motion.div
                          key={`${option.type}-${option.amount}-${index}`}
                          className={`relative bg-gradient-to-br from-white to-gray-50 rounded-xl border-2 transition-all duration-300 hover:shadow-xl ${
                            option.popular
                              ? activeTab === 'onetime'
                                ? 'border-purple-400 shadow-lg shadow-purple-100'
                                : 'border-green-400 shadow-lg shadow-green-100'
                              : 'border-gray-200 hover:border-purple-300'
                          }`}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          whileHover={{ y: -4 }}
                        >
                          {option.popular && (
                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                              <motion.div
                                className={`bg-gradient-to-r ${
                                  activeTab === 'onetime'
                                    ? 'from-purple-500 to-pink-500'
                                    : 'from-green-500 to-emerald-500'
                                } text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg flex items-center gap-1 whitespace-nowrap`}
                                animate={{
                                  boxShadow: [
                                    "0 4px 14px 0 rgba(168, 85, 247, 0.4)",
                                    "0 4px 20px 0 rgba(168, 85, 247, 0.6)",
                                    "0 4px 14px 0 rgba(168, 85, 247, 0.4)"
                                  ]
                                }}
                                transition={{
                                  duration: 2,
                                  repeat: Infinity,
                                  repeatType: "reverse"
                                }}
                              >
                                <SafeIcon icon={FiStar} className="w-3 h-3" />
                                {t('mostPopular')}
                              </motion.div>
                            </div>
                          )}

                          <div className="p-4 sm:p-6 pt-6">
                            <div className="text-center mb-4">
                              <motion.div
                                className={`w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br ${option.color} rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg`}
                                whileHover={{ rotate: 360 }}
                                transition={{ duration: 0.6 }}
                              >
                                <SafeIcon icon={option.icon} className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                              </motion.div>
                              
                              <h4 className="text-sm sm:text-lg font-semibold text-gray-800 mb-2">
                                {t(option.titleKey)}
                              </h4>
                              
                              <div className="flex items-baseline justify-center gap-1 mb-1">
                                <span className={`text-2xl sm:text-4xl font-bold bg-gradient-to-r ${
                                  activeTab === 'onetime'
                                    ? 'from-purple-600 to-pink-600'
                                    : 'from-green-600 to-emerald-600'
                                } bg-clip-text text-transparent`}>
                                  ${option.amount}
                                </span>
                                {activeTab === 'monthly' && (
                                  <span className="text-gray-500 text-xs sm:text-sm">
                                    / {t('month')}
                                  </span>
                                )}
                              </div>
                              
                              {activeTab === 'monthly' && (
                                <p className="text-xs text-gray-500 mb-3">
                                  {t('manualMonthlyPayment')}
                                </p>
                              )}
                              
                              <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                                {t(option.descKey)}
                              </p>
                            </div>

                            <motion.button
                              className={`w-full py-2 sm:py-3 px-4 sm:px-6 rounded-lg font-semibold text-xs sm:text-base transition-all duration-300 ${
                                option.popular
                                  ? activeTab === 'onetime'
                                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg hover:shadow-xl'
                                    : 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg hover:shadow-xl'
                                  : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 hover:from-gray-200 hover:to-gray-300'
                              }`}
                              onClick={() => handleSupportClick(option.paymentLink)}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              {activeTab === 'onetime' ? t('supportNow') : t('subscribeNow')}
                            </motion.button>
                          </div>
                        </motion.div>
                      ))}
                    </motion.div>
                  </AnimatePresence>

                  {/* Tab 特定說明 */}
                  {activeTab === 'monthly' && (
                    <motion.div
                      className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4 mb-6 text-center"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <p className="text-xs sm:text-sm text-blue-800">
                        💡 {t('monthlyNote')}
                      </p>
                    </motion.div>
                  )}

                  {/* 底部說明 */}
                  <motion.div
                    className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 sm:p-5 border border-blue-200"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiHeart} className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-2">
                          <span className="font-semibold text-gray-800">{t('supportNoticeTitle')}: </span>
                          {t('supportNoticeContent')}
                        </p>
                        <p className="text-xs text-gray-600">
                          {t('supportNoticeFooter')}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SupportPopup;